/** 
 * @file /src/components/demo/relationships/RelationshipsTableDialog.js
 * @description Modal dialog that contains the relationship table
 */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import Legend from '../Legend';
import FinchButton  from '../FinchButton';
import ReactTooltip                     from 'react-tooltip';
import RelationshipFeedbackDialog from '../RelationshipFeedbackDialog';


/**
 * @class
 * @description Modal dialog that contains the relationship table
 * @property relationships - Array of relationship objects
 */
class RelationshipsTableDialog extends Component {

    /** @constructor */
    constructor(props){
        super(props);

        this.close = this.close.bind(this);
        this.open = this.open.bind(this);
        this.sortRelationships = this.sortRelationships.bind(this);
        this.exportData = this.exportData.bind(this);
        this.filterRelationships = this.filterRelationships.bind(this);
        this.highlightRow = this.highlightRow.bind(this);
        this.unhighlightRow = this.unhighlightRow.bind(this);
        this.handleFeedback = this.handleFeedback.bind(this);

        this.state = {
            error: '',
            modalOpen: false,
            highlightedRow: null,
            relationships: this.props.relationships.map((v, inx)=>(Object.assign({},v,{inx}))),
            selected: Array(this.props.relationships.length).fill(true),
        };
    };

    componentDidMount() {
        ReactModal.setAppElement('#rm_RelationshipsTableDialog');
    }

    /**
     * Close the dialog box
     */
    close() {
        this.setState({ modalOpen: false });
    };

    /**
     * Open the dialog box
     */
    open() {
        this.setState({
            modalOpen: true,
            error: '',
            relationships: this.props.relationships.map((v, inx)=>(Object.assign({},v,{inx}))),
            selected: Array(this.props.relationships.length).fill(true),
        })
    };
    sortRelationships(e, v){
        e.preventDefault();
    }
    filterRelationships(selectedInx){
        if (selectedInx === -1){
            const fill= !this.state.selected.every((element) => element)
            this.setState({selected: this.state.selected.map(()=>fill)});
        }
        else {
            this.setState({selected: this.state.selected.map((v,inx)=>(selectedInx===inx)!==v)})
        }
    }

    highlightRow(rowInx){
        this.setState({highlightedRow: rowInx});
    }

    unhighlightRow(rowInx){
        if (rowInx === this.state.highlightedRow){
            this.setState({highlightedRow: null});
        }
    }

    exportData(){        
        const output = Object.assign({},this.props.json)
        const filtered_rels = this.state.relationships.filter((r,inx)=>this.state.selected[inx])
        output['relationships'] = filtered_rels

        // const { myData } = this.state; // I am assuming that "this.state.myData"
                                       // is an object and I wrote it to file as
                                       // json
      
        // create file in browser
        const fileName = 'relationship-table';
        const json = JSON.stringify(output, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const href = URL.createObjectURL(blob);
      
        // create "a" HTLM element with href to file
        const link = document.createElement('a');
        link.href = href;
        link.download = fileName + '.json';
        document.body.appendChild(link);
        link.click();
      
        // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }
      
    handleFeedback(r) {
        var dlg = this.refs['rel-feedback-dlg'];
        if (dlg && r) {
            dlg.open(r);
        }
    }

    render() {

        const styles = {
            overlay: {
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                overflow: 'hidden',
                height: '2000px',
                zIndex: '1000'
            },
            content : {
                top         : '35%',
                left        : '50%',
                overflow    : 'hidden',
                marginRight : '-50%',
                paddingTop  : '35px',
                transform   : 'translate(-50%, -100%)',
                maxWidth    : '80%',
                height      : '35%',
                padding     : '0px',
                opacity     : '0.99',
                border      : '0px',
                background  : 'transparent',
                borderRadius: '0px 0px 5px 5px'
            }
        }

        const refFun = (el) => {
            if (el) {
              el.indeterminate = this.state.selected.some((element) => element) && !this.state.selected.every((element) => element);
            }
          };
        
        return (
           <span id="rm_RelationshipsTableDialog">
                <button className="link-button" onClick={this.open}>
                    <span className='fa fa-table'></span>{this.props.iconOnly ? '' : ' Table'}
                </button>
                <ReactModal ref="relationshipsId" 
                    isOpen={this.state.modalOpen}
                    contentLabel="Relationships"
                    onRequestClose={this.close} 
                    style={styles}
                    closeTimeoutMS={250}>
                    {!this.feedbackDisable &&
                        <RelationshipFeedbackDialog text={this.props.text} results={this.props.json} ref="rel-feedback-dlg"/>
                    }
                    <div className="relationships-dialog" style={{overflowY:'hidden'}}>
                        <div className="relationships-header">
                            <span>
                                Relationships
                            </span>
                        </div>
                        <div className="relationships-description" style={{paddingBottom:'40px'}}>
                            <span className='fa fa-table relationships-icon'>&nbsp;</span>
                            Table view of relationships extracted from text.    
                            <span style={{float:'right', paddingRight:'20px'}}>
                                <FinchButton submit={this.exportData} buttonClasses="show-results-button" 
                                  inactive={!this.state.selected.some((v)=>v)} primary={true} text={"Export"} />
                            </span>
                        </div>
                        <div className="relationships-content" style={{padding: '0px'}}>
                            <div className="relationships-form">
                                <div className="relationship-table" style = {{
                                    width: '100%', 
                                    padding: '10px',
                                    height: '450px',
                                    overflow: 'auto'
                                    }}>
                                    <ReactTooltip id="feedback_tooltip" type="dark" effect="solid" place="right">
                                        Relationship Feedback
                                    </ReactTooltip>
                                    <ReactTooltip id="export_tooltip" type="dark" effect="solid" place="right">
                                        Check to Export
                                    </ReactTooltip>
                                    <table>
                                        <thead>
                                            <tr style={{backgroundColor: '#fff', '&:hover': {backgroundColor: '#999',}}} >
                                                <th style={{borderRight: '1px solid rgb(0, 0, 0)', textAlign:'center'}}>
                                                    <input type="checkbox" 
                                                        ref={refFun}
                                                        style={{cursor: 'pointer'}}
                                                        checked={this.state.selected.every((v)=>v)} 
                                                        onChange={()=>this.filterRelationships(-1)}/>
                                                        {/* Add a feedback icon with alt text that says "Relationship Feedback" */}
                                                        {/* Add a tooltip/alt text to checkbox that says "Check to export" */}
                                                </th>
                                                <th onClick={(e)=>this.sortRelationships(e, 'Subject')}>
                                                    <button className="link-button" >
                                                        Subject
                                                    </button>
                                                </th>
                                                <th onClick={(e)=>this.sortRelationships(e, 'Subject Type')}>
                                                    <button className="link-button" >
                                                        Subject Type
                                                    </button>
                                                </th>
                                                <th onClick={(e)=>this.sortRelationships(e, 'Predicate')}>
                                                    <button className="link-button" >
                                                        Predicate
                                                    </button>
                                                </th>
                                                <th onClick={(e)=>this.sortRelationships(e, 'Object')}>
                                                    <button className="link-button" >
                                                        Object
                                                    </button>
                                                </th>
                                                <th onClick={(e)=>this.sortRelationships(e, 'Object Type')}>
                                                    <button className="link-button" >
                                                        Object Type
                                                    </button>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.state.relationships.map((r)=>
                                                <tr 
                                                    key={r.inx} 
                                                    style={{backgroundColor: r.inx === this.state.highlightedRow ? '#f9f9f9' : 'white'}}
                                                    onMouseEnter={()=>this.highlightRow(r.inx)}
                                                    onMouseLeave={()=>this.unhighlightRow(r.inx)}
                                                >
                                                    <td style={{width:'10%', borderRight: '1px solid rgb(0, 0, 0)', textAlign:'center'}}>
                                                        <input data-tip data-for="export_tooltip" type="checkbox" checked={this.state.selected[r.inx]} onChange={()=>this.filterRelationships(r.inx)} style={{cursor: 'pointer'}}/>
                                                        &nbsp;
                                                        <span data-tip data-for="feedback_tooltip" className="fa fa-comments-o" style={{marginLeft:'15px', cursor: 'pointer'}} onClick={()=>this.handleFeedback(r)}></span>
                                                    </td>
                                                    <td style={{width:'25%'}}>{r.subject.label}</td>
                                                    <td style={{width:'10%'}}>{r.subject.type}</td>
                                                    <td style={{width:'20%'}}>{r.predicate.type}</td>
                                                    <td style={{width:'25%'}}>{r.object.label}</td>
                                                    <td style={{width:'10%'}}>{r.object.type}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="relationships-footer">
                            <button type="button" className="relationships-default-button" onClick={this.close}>Close</button>
                        </div>
                    </div>
                </ReactModal>
            </span>
        );
    };
}

RelationshipsTableDialog.propTypes = {
    relationships: PropTypes.array.isRequired
};

export default RelationshipsTableDialog;


